import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { GlobalSetting, SystemSettingKeysResponse } from '@/types/api';
import { canManageGlobalSettings } from '@/utils/roleUtils';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import { FormField } from '@/components/molecules/FormField';
import Loader from '@/components/atoms/Loader/Loader';
import './GlobalSettings.scss';

const GlobalSettings: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [settings, setSettings] = useState<GlobalSetting[]>([]);
  const [systemKeys, setSystemKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSetting, setEditingSetting] = useState<GlobalSetting | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | null>(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // Check permissions
  if (!canManageGlobalSettings(user)) {
    return (
      <div className="global-settings-page">
        <div className="container">
          <Card variant="elevated" padding="lg">
            <p>{t('errors.unauthorized')}</p>
          </Card>
        </div>
      </div>
    );
  }

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const params: { category?: string; is_active?: boolean } = {};
      if (categoryFilter) params.category = categoryFilter;
      if (isActiveFilter !== null) params.is_active = isActiveFilter;
      
      const data = await apiService.getGlobalSettings(params);
      setSettings(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, isActiveFilter, t]);

  const fetchSystemKeys = useCallback(async () => {
    try {
      const response = await apiService.getSystemSettingKeys();
      setSystemKeys(response.system_keys);
    } catch (err: any) {
      console.error('Failed to fetch system keys:', err);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchSystemKeys();
  }, [fetchSettings, fetchSystemKeys]);

  const handleEdit = (setting: GlobalSetting) => {
    setEditingSetting(setting);
  };

  const handleCancelEdit = () => {
    setEditingSetting(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSetting) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const updateData = {
        setting_value: editingSetting.setting_value,
        description: editingSetting.description || null,
        category: editingSetting.category,
        is_active: editingSetting.is_active,
      };

      await apiService.updateGlobalSetting(editingSetting.id, updateData);
      await fetchSettings();
      setEditingSetting(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      setError(null);
      await apiService.syncGlobalSettings();
      await fetchSettings();
      setShowSyncModal(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsSyncing(false);
    }
  };

  const handleVerify = async () => {
    try {
      setIsVerifying(true);
      setError(null);
      const result = await apiService.verifyGlobalSettings();
      if (result.errors && result.errors.length > 0) {
        setError(result.errors.join(', '));
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsVerifying(false);
    }
  };

  const getCategories = () => {
    const categories = new Set<string>();
    settings.forEach(setting => {
      if (setting.category) {
        categories.add(setting.category);
      }
    });
    return Array.from(categories).sort();
  };

  const filteredSettings = settings.filter(setting => {
    if (categoryFilter && setting.category !== categoryFilter) return false;
    if (isActiveFilter !== null && setting.is_active !== isActiveFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="global-settings-page">
        <div className="container">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="global-settings-page">
      <div className="container">
        <div className="global-settings-page__header">
          <h1 className="global-settings-page__title">
            {t('admin.globalSettings.title')}
          </h1>
          <p className="global-settings-page__description">
            {t('admin.globalSettings.description')}
          </p>
        </div>

        {error && (
          <Card variant="elevated" padding="md" className="global-settings-page__error">
            <p>{error}</p>
          </Card>
        )}

        {/* Filters and Actions */}
        <Card variant="elevated" padding="lg" className="global-settings-page__filters">
          <div className="global-settings-page__filters-content">
            <FormField
              type="select"
              name="category"
              label={t('admin.globalSettings.category')}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">{t('admin.globalSettings.allCategories')}</option>
              {getCategories().map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </FormField>

            <FormField
              type="select"
              name="is_active"
              label={t('admin.globalSettings.status')}
              value={isActiveFilter === null ? '' : isActiveFilter.toString()}
              onChange={(e) => {
                const value = e.target.value;
                setIsActiveFilter(value === '' ? null : value === 'true');
              }}
            >
              <option value="">{t('admin.globalSettings.allStatuses')}</option>
              <option value="true">{t('admin.globalSettings.active')}</option>
              <option value="false">{t('admin.globalSettings.inactive')}</option>
            </FormField>

            <div className="global-settings-page__actions">
              <Button
                variant="primary"
                onClick={() => setShowSyncModal(true)}
                disabled={isSyncing}
              >
                {isSyncing ? t('common.loading') : t('admin.globalSettings.sync')}
              </Button>
              <Button
                variant="outline"
                onClick={handleVerify}
                disabled={isVerifying}
              >
                {isVerifying ? t('common.loading') : t('admin.globalSettings.verify')}
              </Button>
            </div>
          </div>
        </Card>

        {/* Settings List */}
        <div className="global-settings-page__list">
          {filteredSettings.map((setting) => (
            <Card key={setting.id} variant="elevated" padding="lg" className="global-settings-page__setting-card">
              {editingSetting?.id === setting.id ? (
                <form onSubmit={handleSave} className="global-settings-page__edit-form">
                  <div className="global-settings-page__form-fields">
                    <FormField
                      type="text"
                      name="setting_key"
                      label={t('admin.globalSettings.settingKey')}
                      value={editingSetting.setting_key}
                      disabled
                    />
                    <FormField
                      type={editingSetting.setting_type === 'boolean' ? 'select' : editingSetting.setting_type === 'number' ? 'number' : 'text'}
                      name="setting_value"
                      label={t('admin.globalSettings.settingValue')}
                      value={editingSetting.setting_value}
                      onChange={(e) => setEditingSetting({
                        ...editingSetting,
                        setting_value: e.target.value
                      })}
                      required
                    >
                      {editingSetting.setting_type === 'boolean' && (
                        <>
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </>
                      )}
                    </FormField>
                    <FormField
                      type="text"
                      name="description"
                      label={t('admin.globalSettings.description')}
                      value={editingSetting.description || ''}
                      onChange={(e) => setEditingSetting({
                        ...editingSetting,
                        description: e.target.value
                      })}
                    />
                    <FormField
                      type="text"
                      name="category"
                      label={t('admin.globalSettings.category')}
                      value={editingSetting.category}
                      onChange={(e) => setEditingSetting({
                        ...editingSetting,
                        category: e.target.value
                      })}
                      required
                    />
                    <FormField
                      type="select"
                      name="is_active"
                      label={t('admin.globalSettings.isActive')}
                      value={editingSetting.is_active.toString()}
                      onChange={(e) => setEditingSetting({
                        ...editingSetting,
                        is_active: e.target.value === 'true'
                      })}
                    >
                      <option value="true">{t('common.yes')}</option>
                      <option value="false">{t('common.no')}</option>
                    </FormField>
                  </div>
                  <div className="global-settings-page__form-actions">
                    <Button type="submit" variant="primary" disabled={isSubmitting}>
                      {t('common.save')}
                    </Button>
                    <Button type="button" variant="outline" onClick={handleCancelEdit}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="global-settings-page__setting-header">
                    <div>
                      <h3 className="global-settings-page__setting-key">{setting.setting_key}</h3>
                      {setting.description && (
                        <p className="global-settings-page__setting-description">{setting.description}</p>
                      )}
                    </div>
                    <div className="global-settings-page__setting-badges">
                      <span className={`global-settings-page__badge global-settings-page__badge--${setting.setting_type}`}>
                        {setting.setting_type}
                      </span>
                      <span className={`global-settings-page__badge global-settings-page__badge--${setting.is_active ? 'active' : 'inactive'}`}>
                        {setting.is_active ? t('admin.globalSettings.active') : t('admin.globalSettings.inactive')}
                      </span>
                      {setting.category && (
                        <span className="global-settings-page__badge global-settings-page__badge--category">
                          {setting.category}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="global-settings-page__setting-value">
                    <strong>{t('admin.globalSettings.value')}:</strong> {setting.setting_value}
                  </div>
                  <div className="global-settings-page__setting-actions">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(setting)}>
                      {t('common.edit')}
                    </Button>
                  </div>
                </>
              )}
            </Card>
          ))}
        </div>

        {filteredSettings.length === 0 && !loading && (
          <Card variant="elevated" padding="lg">
            <p>{t('admin.globalSettings.noSettings')}</p>
          </Card>
        )}

        {/* Sync Modal */}
        {showSyncModal && (
          <div className="global-settings-page__modal-overlay" onClick={() => setShowSyncModal(false)}>
            <div onClick={(e) => e.stopPropagation()}>
              <Card variant="elevated" padding="lg" className="global-settings-page__modal">
                <h3>{t('admin.globalSettings.syncConfirm')}</h3>
                <p>{t('admin.globalSettings.syncConfirmMessage')}</p>
                <div className="global-settings-page__modal-actions">
                  <Button variant="primary" onClick={handleSync} disabled={isSyncing}>
                    {isSyncing ? t('common.loading') : t('common.confirm')}
                  </Button>
                  <Button variant="outline" onClick={() => setShowSyncModal(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSettings;

